import {
  bit,
  defaultSource,
  flag,
  readSheetSource,
  type SheetSourceState,
  writeSheetSource,
} from "./worksheet-state.js";

/**
 * Settings for a sheet made of parts that are each ticked or not, and where
 * they live in the address.
 *
 * The look-alike and vocabulary sheets are both a source, a paper and a tick
 * box per part, so one reader and writer serves both, built from the parts'
 * names and defaults. Where the words come from is read by
 * `worksheet-state.ts`, so a List's link opens any sheet on it.
 */

/** A sheet's settings: its source and paper, and whether each part prints. */
export type PartState<Part extends string> = SheetSourceState &
  Readonly<Record<Part, boolean>>;

/** Reads and writes one kind of sheet's settings. */
export class PartStates<Part extends string> {
  /** The settings of a sheet nobody has touched. */
  readonly defaults: PartState<Part>;

  readonly #parts: readonly Part[];

  /**
   * @param parts - Each part's name, as the address writes it, with whether
   * it prints by default. The order is the order the sheet prints them in.
   */
  constructor(parts: readonly (readonly [Part, boolean])[]) {
    this.#parts = parts.map(([part]) => part);
    this.defaults = {
      ...defaultSource,
      ...Object.fromEntries(parts),
    } as PartState<Part>;
  }

  /** The settings in an address fragment, with or without its `#`. */
  read(hash: string): PartState<Part> {
    const parameters = new URLSearchParams(hash.replace(/^#/u, ""));

    return {
      ...readSheetSource(parameters),
      ...Object.fromEntries(
        this.#parts.map((part) => [
          part,
          flag(parameters.get(part), this.defaults[part]),
        ]),
      ),
    } as PartState<Part>;
  }

  /** The fragment for the settings, holding only what differs from the defaults. */
  fragment(state: PartState<Part>): string {
    const parameters = new URLSearchParams();
    writeSheetSource(parameters, state);
    for (const part of this.#parts) {
      if (state[part] !== this.defaults[part]) {
        parameters.set(part, bit(state[part]));
      }
    }

    return parameters.toString();
  }

  /** Whether the settings ask for any part at all. */
  asksForSomething(state: PartState<Part>): boolean {
    return this.#parts.some((part) => state[part]);
  }
}
